import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencilAlt,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import useApi from "../../../Hooks/useApi.js";
import showToast from "../../../Helpers/toast.js";
import TextField from "../../UI/Inputs/TextField.js";
import {
  deleteSection,
  updateSection,
} from "../../../Utils/questionnaireUtils.js";

const SectionListMenu = ({
  sectionList,
  setSectionList,
  selectedSection,
  setSelectedSection,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editedSectionName, setEditedSectionName] = useState("");
  const [sectionEditMode, setSectionEditMode] = useState(false);
  const sectionListRef = useRef(null);
  const editRef = useRef(null);
  const { request } = useApi();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editRef.current && !editRef.current.contains(event.target)) {
        setEditingSectionId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const slideLeft = () => {
    if (sectionListRef.current && sectionList.length > 0) {
      sectionListRef.current.scrollBy({ left: -128, behavior: "smooth" });
      setSelectedIndex((prevIndex) => Math.max(0, prevIndex - 1));
    }
  };

  const handleSectionSelection = (sectionId) => {
    setSelectedSection(sectionId);
  };

  const slideRight = () => {
    if (sectionListRef.current && sectionList.length > 0) {
      sectionListRef.current.scrollBy({ left: 128, behavior: "smooth" });
      setSelectedIndex((prevIndex) =>
        Math.min(sectionList.length - 1, prevIndex + 1)
      );
    }
  };

  const handleEditSection = (sectionId, currentName) => {
    setEditingSectionId(sectionId);
    setEditedSectionName(currentName);
    setSectionEditMode(true);
  };

  const handleSaveEdit = async (sectionId) => {
    try {
      const response = await updateSection(request, {
        sectionId,
        sectionName: editedSectionName,
        sectionOrder: sectionList.find((s) => s.sectionId === sectionId)
          .sectionOrder,
        sectionStatus: "ACTIVE",
        required: true,
      });
      setSectionEditMode(false);
      setSectionList((prevList) =>
        prevList.map((section) =>
          section.sectionId === sectionId
            ? { ...section, name: editedSectionName }
            : section
        )
      );
      setEditingSectionId(response.sectionId);
      handleSectionSelection(response.sectionId);

      showToast({ message: "Section name updated successfully!" });
    } catch (error) {
      showToast({
        message: error?.response?.data?.message || "Error Occured!",
        type: "error",
      });
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (window.confirm("Are you sure you want to delete this section?")) {
      try {
        const response = await deleteSection(request, sectionId);
        setSectionList((prevList) =>
          prevList.filter((section) => section.sectionId !== sectionId)
        );
        if (selectedSection === sectionId) {
          handleSectionSelection(null);
        }

        showToast({ message: response.message });
      } catch (error) {
        showToast({ message: error?.response?.data?.message, type: "error" });
      }
    }
  };

  return (
    <div className="flex items-center gap-2 ">
      <button
        onClick={slideLeft}
        disabled={selectedIndex === 0}
        className="disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>

      <div
        ref={sectionListRef}
        className="flex items-center gap-2 overflow-auto whitespace-nowrap 
                scrollbar-thumb-gray-500 w-full"
      >
        {sectionList && sectionList?.length ? (
          sectionList.map((section) => (
            <div
              key={section.sectionId}
              className={`flex items-center gap-2 px-2 rounded-md py-1 
                            ${
                              section?.sectionId === selectedSection
                                ? `bg-blue-txt text-blue-secondary`
                                : "bg-blue-txt bg-opacity-10"
                            } 
                            hover:cursor-pointer`}
            >
              {sectionEditMode && editingSectionId === section.sectionId ? (
                <div
                  ref={editRef}
                  onKeyDown={(e) => {
                    e.key === "Enter" && handleSaveEdit(section.sectionId);
                  }}
                >
                  <TextField
                    value={editedSectionName}
                    onValueChange={(_, val) => setEditedSectionName(val)}
                    onBlur={() => handleSaveEdit(section.sectionId)}
                  />
                </div>
              ) : (
                <>
                  <span
                    onClick={() => handleSectionSelection(section?.sectionId)}
                  >
                    {section?.name}
                  </span>
                  {section.sectionId > 0 ? (
                    <>
                      <FontAwesomeIcon
                        icon={faPencilAlt}
                        onClick={() =>
                          handleEditSection(section.sectionId, section.name)
                        }
                        className="text-gray-500 hover:text-blue-500 cursor-pointer"
                      />
                      <FontAwesomeIcon
                        icon={faTrash}
                        onClick={() => handleDeleteSection(section.sectionId)}
                        className="text-gray-500 hover:text-red-500 cursor-pointer"
                      />
                    </>
                  ) : (
                    <></>
                  )}
                </>
              )}
            </div>
          ))
        ) : (
          <></>
        )}
      </div>

      <button
        onClick={slideRight}
        disabled={selectedIndex === sectionList.length - 1}
        className="disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
};

export default SectionListMenu;
